/**
 * acequia.createGrantClient() — the holder's side of an app's grant flow
 * (unified-token-system, Phase 2 as narrowed; the counterpart of
 * createGrantManager).
 *
 * A viewer that wants access to a peer's app — a camera, a console, a board —
 * asks for it, keeps the grant it is given, presents it again when it
 * reconnects, takes renewals, and drops it when it is revoked. CameraMap, the
 * GeoCamera remote and the debug-console receiver each wrote that; this is
 * it once. The app supplies how it is named to the grantor and what it asks
 * for.
 *
 *   request(peerId, { scope })   an accessRequest to the grantor's tab, resolved
 *                                by its accessResponse: the grant record, or a
 *                                refusal ('denied', 'scope-limited', …)
 *   present(peerId)              a held grant presented again (tokenPresent, with
 *                                its anchors, so the grantor checks it offline)
 *   ensure(peerId, { scope })    present a held grant, or request one
 *   grantFor(peerId)             the grant held for a peer (by its device)
 *   fetch(peerId, path, init)    a request to the grantor's route, the grant's id
 *                                as the bearer (/groups/<group>/<peer>/<path>)
 *   handleMessage(peerId, data)  accessResponse, tokenRenewal, accessRevoked
 *
 * Grants are peer-token records (origin 'granted', keyed by the grantor's
 * device id) with the grant set the grantor signed and the anchors it sent.
 * A record has an id of its own; the grantor's id for the grant — what is
 * presented, and the bearer on its routes — is `grantId`. (A holder that
 * reused the grantor's id, as the hand-written holders did, had its renewals
 * and revocations land on the grantor's own record whenever both lived in
 * one browser: the minted store is searched first.)
 *
 * Events (EventTarget): 'granted', 'denied', 'renewed', 'revoked', 'change'.
 */

import { buildAccessRequest, buildTokenPresent } from './tokenProtocol.js'
import * as peerTokens from './peerTokens.js'
import * as core from '../acequia/acequia.js'

const DEFAULT_TIMEOUT = 0 // a request waits for its answer: the owner may take minutes
const GRANTED = { origin: 'granted' } // every store call names the holder's store (see peerTokens.findById)

export class GrantClient extends EventTarget {
    /**
     * @param {object} opts
     * @param {object} opts.group - the acequia Group the grantor is in
     * @param {string} opts.aud - the app asked ('geocamera', 'debug-console', 'sharedboard')
     * @param {string} [opts.tag] - the records' tag (default: aud)
     * @param {string} [opts.scope] - what to ask for by default (an app's preset word, or a grant set)
     * @param {string|() => string} [opts.displayName] - how the grantor's owner sees this viewer
     * @param {string} [opts.baseUrl] - where the grantor's routes are served (default: this page's origin)
     * @param {typeof fetch} [opts.fetch]
     * @param {number} [opts.timeoutMs=0] - give up on a request after this (0: never)
     */
    constructor(opts) {
        super()
        if (!opts?.group) throw new Error('createGrantClient: a group is required')
        if (!opts.aud) throw new Error('createGrantClient: an audience (aud) is required')
        this.group = opts.group
        this.aud = opts.aud
        this.tag = opts.tag || opts.aud
        this.scope = opts.scope || 'full'
        this._displayName = opts.displayName || null
        this.baseUrl = opts.baseUrl || (typeof location !== 'undefined' ? location.origin : '')
        this._fetch = opts.fetch || (typeof fetch === 'function' ? fetch.bind(globalThis) : null)
        this.timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT
        this.cache = null // PeerTokenCache of this app's granted records, by the grantor's device id
        this._waiting = new Map() // peerId → { resolve, reject, timer }
        this._unsubs = []
    }

    async start() {
        await core.acequiaReady()
        // Live records only: a revoked grant is gone from here (the components read the store for history)
        this.cache = new peerTokens.PeerTokenCache({ origin: 'granted', groupName: this._groupName(), tags: [this.tag], status: 'active' })
        await this.cache.hydrate()
        this._unsubs.push(peerTokens.onTokenChange(({ record }) => {
            if (!record || (record.origin === 'granted' && record.tags?.includes(this.tag))) this._changed()
        }))
        this._changed()
        return this
    }

    destroy() {
        for (const u of this._unsubs) { try { u() } catch {} }
        this._unsubs = []
        for (const [, w] of this._waiting) { clearTimeout(w.timer); w.reject(new Error('client destroyed')) }
        this._waiting.clear()
        this.cache?.destroy?.()
    }

    _groupName() {
        return this.group.groupId || this.group.groupName || null
    }

    _changed() {
        this.dispatchEvent(new CustomEvent('change'))
    }

    _emit(type, detail) {
        this.dispatchEvent(new CustomEvent(type, { detail }))
        this._changed()
    }

    get displayName() {
        if (typeof this._displayName === 'function') return this._displayName()
        return this._displayName || this.group.displayName || (typeof document !== 'undefined' ? document.title : 'viewer')
    }

    /** The grantor's device id for a peer (its tab), from the group's peers list or a held record. */
    deviceOf(peerId) {
        const info = this.group.peersList?.[peerId]
        if (info?.deviceId) return info.deviceId
        for (const r of this.cache?.values?.() || []) if (r.peerId === peerId) return r.deviceId
        return null
    }

    /** The grant held for a peer, if any (live or renewable, or not). */
    grantFor(peerId) {
        const deviceId = this.deviceOf(peerId)
        return (deviceId && this.cache?.get(deviceId)) || null
    }

    /** The grantor's id for a held grant: what is presented, and the bearer on its routes. */
    bearerOf(record) {
        return record?.grantId || record?.id || null
    }

    _byGrantId(grantId) {
        for (const r of this.cache?.values?.() || []) if (r.grantId === grantId || r.id === grantId) return r
        return null
    }

    /** Whether a record still serves: unexpired, or renewable by its grantor. */
    isLive(record) {
        if (!record || record.status !== 'active') return false
        return !record.expiresAt || Date.parse(record.expiresAt) > Date.now() || !!record.autoRenew
    }

    /** Every grant this client holds. */
    list() {
        return [...(this.cache?.values?.() || [])]
    }

    // ── asking ───────────────────────────────────────────────────────

    /** Who this viewer is, as the grantor's prompt shows it. */
    _identity() {
        return {
            deviceId: core.getDeviceId(),
            userId: core.getUserId(),
            viewerId: core.getInstanceId(),
            viewerName: this.displayName,
            handle: core.getHandle?.() || null,
            deviceName: core.getDeviceName?.() || null,
        }
    }

    /**
     * Ask a peer's app for access. Resolves with the grant record when the
     * owner grants (now, or later: a request left pending is answered when
     * the owner gets to it), rejects with the refusal's reason.
     * @param {string} peerId
     * @param {object} [opts]
     * @param {string|object} [opts.scope] - what to ask for
     * @param {object} [opts.extra] - the app's own request fields (GeoCamera: camera, upgradeFrom)
     * @returns {Promise<object>} the grant record
     */
    request(peerId, { scope = this.scope, extra = {} } = {}) {
        const requestId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
        const msg = { ...buildAccessRequest({ ...this._identity(), scope, aud: this.aud }), aud: this.aud, requestId, ...extra }
        const { deviceName } = this._identity()
        if (deviceName) msg.deviceName = deviceName
        return this._awaitAnswer(peerId, requestId, () => this._send(peerId, msg))
    }

    /**
     * Present the grant held for a peer, so it knows this viewer again (a
     * reconnect, a reload). Resolves with the grant as confirmed or renewed by
     * the grantor; rejects when refused. Resolves null when nothing is held.
     * @param {string} peerId
     * @param {object} [opts]
     * @param {object} [opts.record] - the record to present, when the app resolves it itself
     *   (CameraMap knows which browser a tab is from its shared state)
     */
    present(peerId, { record = this.grantFor(peerId) } = {}) {
        if (!record) return Promise.resolve(null)
        const requestId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
        const { deviceId, userId, viewerId } = this._identity()
        const msg = {
            ...buildTokenPresent({ deviceId, userId, viewerId, token: record.jwt, tokenId: this.bearerOf(record), anchors: record.anchors }),
            aud: this.aud,
            requestId,
            viewerName: this.displayName,
            deviceName: this._identity().deviceName,
        }
        if (Array.isArray(record.anchors) && record.anchors.length) msg.anchors = record.anchors
        return this._awaitAnswer(peerId, requestId, () => this._send(peerId, msg))
    }

    /** A grant for a peer: the one held (presented again), else a new request. */
    async ensure(peerId, opts = {}) {
        const held = this.grantFor(peerId)
        if (held && this.isLive(held)) {
            try {
                return await this.present(peerId)
            } catch (err) {
                // Refused: the grant is gone there (revoked, or the store was lost); ask afresh
                if (!/denied/i.test(err.message)) await peerTokens.revokePeerToken(held.id, GRANTED).catch(() => {})
                else throw err
            }
        }
        return this.request(peerId, opts)
    }

    _awaitAnswer(peerId, requestId, send) {
        return new Promise((resolve, reject) => {
            const prev = this._waiting.get(peerId)
            if (prev) { clearTimeout(prev.timer); prev.reject(new Error('superseded by a newer request')) }
            const timer = this.timeoutMs > 0 ? setTimeout(() => {
                this._waiting.delete(peerId)
                reject(new Error('timed out waiting for the owner'))
            }, this.timeoutMs) : null
            this._waiting.set(peerId, { resolve, reject, timer, requestId })
            Promise.resolve().then(send).catch((err) => {
                clearTimeout(timer)
                this._waiting.delete(peerId)
                reject(err)
            })
        })
    }

    _settle(peerId, requestId, ok, value) {
        const w = this._waiting.get(peerId)
        if (!w) return
        if (requestId && w.requestId && requestId !== w.requestId) return
        clearTimeout(w.timer)
        this._waiting.delete(peerId)
        ok ? w.resolve(value) : w.reject(value)
    }

    async _send(peerId, msg) {
        const r = this.group.sendToPeer(peerId, msg)
        if (r?.catch) await r
    }

    // ── answers ──────────────────────────────────────────────────────

    /** Whether a message is this app's: it names the audience, or names none (a grantor from before audiences). */
    isForMe(data) {
        return !data?.aud || data.aud === this.aud
    }

    /**
     * Handle a peer message for the grant flow: accessResponse, tokenRenewal,
     * accessRevoked. @returns {Promise<boolean>} whether it was this client's
     */
    async handleMessage(peerId, data) {
        if (!data?.type || !this.isForMe(data)) return false
        if (data.type === 'accessResponse') {
            if (data.granted) {
                const record = await this._store(peerId, data)
                this._settle(peerId, data.requestId, true, record)
                this._emit('granted', { peerId, record })
            } else {
                const reason = data.reason || 'denied'
                this._settle(peerId, data.requestId, false, Object.assign(new Error(data.message || reason), { reason }))
                this._emit('denied', { peerId, reason, message: data.message || null })
            }
            return true
        }
        if (data.type === 'tokenRenewal') {
            const held = data.tokenId && this._byGrantId(data.tokenId)
            if (held && data.token) {
                await peerTokens.renewPeerToken(held.id, data.token, data.expiresAt ? new Date(data.expiresAt).toISOString() : held.expiresAt, GRANTED).catch(() => {})
                await this.cache?.hydrate?.()
                this._emit('renewed', { peerId, record: this.cache?.get(held.deviceId) || held })
            }
            return true
        }
        if (data.type === 'accessRevoked') {
            const held = data.tokenId && this._byGrantId(data.tokenId)
            if (held) {
                await peerTokens.revokePeerToken(held.id, GRANTED).catch(() => {})
                this._emit('revoked', { peerId, record: held, tokenId: data.tokenId })
            }
            return true
        }
        return false
    }

    /** Keep a granted token: one record per grantor device, replaced when the grantor minted anew. */
    async _store(peerId, data) {
        const deviceId = data.cameraDeviceId || this.deviceOf(peerId) || peerId
        const preset = typeof data.scope === 'string' ? data.scope : null
        const existing = this.cache?.get(deviceId)
        if (existing && data.tokenId && this.bearerOf(existing) !== data.tokenId) {
            await peerTokens.removePeerToken(existing.id, GRANTED).catch(() => {})
        } else if (existing && data.tokenId && this.bearerOf(existing) === data.tokenId && data.token) {
            await peerTokens.renewPeerToken(existing.id, data.token, data.expiresAt ? new Date(data.expiresAt).toISOString() : existing.expiresAt, GRANTED)
            if (data.grants || data.anchors) await peerTokens.updatePeerToken(existing.id, { ...(data.grants ? { scope: data.grants } : {}), ...(data.anchors ? { anchors: data.anchors } : {}) }, GRANTED).catch(() => {})
            await this.cache?.hydrate?.()
            return this.cache?.get(deviceId) || existing
        }
        const id = await peerTokens.createPeerToken({
            jwt: data.token,
            name: `${data.cameraName || peerId.slice(0, 12)}${preset ? ` (${preset})` : ''}`,
            scope: data.grants && typeof data.grants === 'object' ? data.grants : (data.scope || null),
            tags: [this.tag, ...(preset ? [preset] : [])],
            origin: 'granted',
            peerId,
            deviceId,
            groupName: this._groupName(),
            expiresAt: data.expiresAt ? new Date(data.expiresAt).toISOString() : null,
            autoRenew: true,
        })
        // createPeerToken keeps a fixed set of fields: the anchors (and the app's own response fields) go on after
        const extra = { grantId: data.tokenId || null }
        if (Array.isArray(data.anchors) && data.anchors.length) extra.anchors = data.anchors
        if (data.aud) extra.aud = data.aud
        if (Object.keys(extra).length) await peerTokens.updatePeerToken(id, extra, GRANTED).catch(() => {})
        await this.cache?.hydrate?.()
        return this.cache?.get(deviceId) || peerTokens.getPeerToken(id)
    }

    // ── using it ─────────────────────────────────────────────────────

    /** The URL of a grantor's route: /groups/<group>/<peer>/<path> at the base. */
    routeUrl(peerId, path) {
        const p = String(path || '').replace(/^\/+/, '')
        return `${this.baseUrl}/groups/${encodeURIComponent(this._groupName() || '')}/${encodeURIComponent(peerId)}/${p}`
    }

    /** fetch() against a grantor's route with the held grant as the bearer. */
    async fetch(peerId, path, init = {}) {
        if (!this._fetch) throw new Error('fetch is not available here')
        const record = this.grantFor(peerId)
        const headers = new Headers(init.headers || {})
        if (record) headers.set('Authorization', `Bearer ${this.bearerOf(record)}`)
        return this._fetch(this.routeUrl(peerId, path), { ...init, headers })
    }

    /** Drop the grant held for a peer (this side only) — or a record given directly. */
    async forget(peerIdOrRecord) {
        const record = typeof peerIdOrRecord === 'object' ? peerIdOrRecord : this.grantFor(peerIdOrRecord)
        if (record?.id) await peerTokens.removePeerToken(record.id, GRANTED).catch(() => {})
        await this.cache?.hydrate?.()
        this._changed()
    }

    /** The record held for a grantor's device id (what the hand-written holders keyed by). */
    grantForDevice(deviceId) {
        return (deviceId && this.cache?.get(deviceId)) || null
    }
}

/**
 * Create a holder's grant client, and start it.
 * @param {object} opts - see GrantClient
 * @returns {Promise<GrantClient>}
 */
export async function createGrantClient(opts) {
    return new GrantClient(opts).start()
}
